/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo } from 'react';
import { Alerts, Spinner } from '@project/components';
import PageContentContainer from './../../components/PageContentContainer/PageContentContainer';
import { useIntl } from 'react-intl';
import Title from '../../components/Title/Title';
import { TeamMemberEmail } from './components/TeamMemberEmail';
import { TeamMemberMultiFormAuth } from './components/TeamMemberMultiFormAuth';
import { TeamMemberCompanyTitle } from './components/TeamMemberCompanyTitle';
import { TeamMemberRole } from './components/TeamMemberRole';
import { TeamMemberProperties } from './components/TeamMemberProperties';
import { FormProvider, useForm } from 'react-hook-form';
import { TeamMemberCompanySelector } from './components/TeamMemberCompanySelector';
import { useCurrentUserActions } from './hooks/useCurrentUserActions';
import { INVITE_ROLE, useSaveInvite } from './hooks/useSaveInvite';
import { useInviteTeamMember } from './InviteTeamMemberContext';
import { roleOptions, riversideRoleOptions } from '../../constants';
import { useCompany } from './hooks/useCompany';

const InviteTeamMemberForm = props => {
  const { initCompany, initDisplayCompany } = props;
  const intl = useIntl();
  const t = id => intl.formatMessage({ id });

  const { errors, setErrors, loading } = useInviteTeamMember();

  const form = useForm({
    defaultValues: {
      company: initCompany,
      displayCompany: initDisplayCompany,
      email: '',
      useMfa: true,
      companyTitle: null,
      companyRole: null,
      isCompanyAdmin: false,
      globalAccess: false,
      allSelected: {
        viewer: true,
        editor: false,
        designer: false
      },
      properties: []
    }
  });

  const { data: company, isLoading } = useCompany(form.watch('company'));
  const isVendorCompany = company?.isVendorCompany || false;

  const { data: currentUserActions } = useCurrentUserActions(form.watch('company'), form.watch('displayCompany'));
  const saveInvite = useSaveInvite(currentUserActions, isVendorCompany);

  useEffect(() => {
    form.setValue('company', initCompany);
    form.setValue('displayCompany', initDisplayCompany);
  }, [initCompany, initDisplayCompany]);

  const forceMfa = useMemo(() => {
    let shouldForceMfa = false;
    if (company) {
      shouldForceMfa = company.requireMfa;
      if (shouldForceMfa) {
        form.setValue('useMfa', true);
      }
    }
    return shouldForceMfa;
  }, [company]);

  if (isLoading) {
    return <Spinner />;
  } else {
    return (
      <FormProvider {...form}>
        <PageContentContainer>
          <Title
            title={t('InviteTeamMember.mainTitle')}
            backRoute={'/users'}
            button={{
              click: form.handleSubmit(saveInvite),
              label: t('InviteTeamMember.inviteMember'),
              disabled: !currentUserActions?.includes(INVITE_ROLE)
            }}
          />

          {errors.length > 0 ? (
            <div style={{ marginTop: '20px' }}>
              <Alerts messages={errors} close={() => setErrors([])} />
            </div>
          ) : null}

          <TeamMemberCompanySelector />
          <TeamMemberEmail />
          <TeamMemberMultiFormAuth forceMfa={forceMfa} />

          {isVendorCompany ? (
            <>
              <TeamMemberRole roleOptions={riversideRoleOptions} />
            </>
          ) : (
            <>
              <TeamMemberCompanyTitle />
              <TeamMemberRole roleOptions={roleOptions} />
              <TeamMemberProperties />
            </>
          )}
        </PageContentContainer>
        {loading ? <Spinner /> : null}
      </FormProvider>
    );
  }
};

export default InviteTeamMemberForm;
